import BigNumber from 'bignumber.js';
import {useMemo} from 'react';
import {locale, MAX_DECIMALS_IN_CRYPTO} from '~/config';
import {useShallowMemo} from '~/library/ui-kit';
import {asCurrency, Currency, CurrencyTrait} from './Currency';
import {usePrimaryCurrency} from './primaryCurrencyAtom';

export function useCurrencyFormat(currency: Currency | CurrencyTrait, options: Options = {}) {
    currency = useMemo(() => asCurrency(currency), [currency]);
    options = useShallowMemo(options);
    return useMemo(() => {
        return createCurrencyFormatter(currency, options);
    }, [currency.code, currency.decimals, options]);
}

export namespace CurrencyFormatter {
    export const make = createCurrencyFormatter;

    export type Fn = (value: BigNumber.Value, opt?: FormatOptions) => string;

    export type FnOptions = FormatOptions;
}

export function createCurrencyFormatter(
    currency: Currency | CurrencyTrait,
    {approx = false, parts: partsShape = ['amount', 'code'], ...numberFormatOptions}: Options = {}
) {
    const formatOptions: Required<FormatOptions> = {approx, parts: partsShape};

    currency = asCurrency(currency);
    const code = currency.code;
    // see MAX_DECIMALS_IN_CRYPTO doc comment
    const decimals = Math.min(currency.decimals, MAX_DECIMALS_IN_CRYPTO);

    // @ts-expect-error it is widely available in all major browsers, but only included in esnext lib
    numberFormatOptions.roundingMode = numberFormatOptions.roundingMode || 'trunc';

    const iso4217Currencies = new Set(Intl.supportedValuesOf('currency'));
    let numberFormat: Intl.NumberFormat;
    if (iso4217Currencies.has(code.toUpperCase())) {
        numberFormat = new Intl.NumberFormat(locale, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
            ...numberFormatOptions,
            currency: code,
            currencyDisplay: 'narrowSymbol',
            style: 'currency',
        });
    } else {
        numberFormat = new Intl.NumberFormat(locale, {
            maximumFractionDigits: decimals,
            // non-ISO currencies (likely crypto currencies) most often should be formatted
            // with insignificant zeros removed
            minimumFractionDigits: 0,
            ...numberFormatOptions,
        });
    }

    function toNumber(value: BigNumber.Value): number {
        if (typeof value === 'number') {
            return value;
        }
        if (BigNumber.isBigNumber(value)) {
            return value.toNumber();
        }
        return Number(value);
    }

    function localeFormat(value: BigNumber.Value) {
        return numberFormat.format(toNumber(value));
    }

    function formatToParts(value: BigNumber.Value): Parts {
        const parts = numberFormat.formatToParts(toNumber(value));
        const symbolPartIndex = parts.findIndex(part => part.type === 'currency');
        const values = parts.map(part => part.value);
        let symbol: string | undefined = undefined;
        if (symbolPartIndex > -1) {
            symbol = values.at(symbolPartIndex);
            values.splice(symbolPartIndex, 1);
        }
        const amount = values
            .map(v => v.trim())
            .filter(v => v !== '')
            .join('');
        return {
            amount,
            symbol,
            code,
        };
    }

    const format: CurrencyFormatter.Fn = function format(
        value,
        {approx = formatOptions.approx, parts: partsShape = formatOptions.parts} = formatOptions
    ) {
        const parts = formatToParts(value);
        // currency symbol is written right next to the amount, without a separating space
        let result = partsShape.reduce((acc, partKey, index) => {
            const part = parts[partKey] ?? '';
            if (index === 0) {
                return part;
            }
            const isSymbolAdjacent = partKey === 'symbol' || partsShape[index - 1] === 'symbol';
            return acc + (isSymbolAdjacent ? '' : ' ') + part;
        }, '');
        if (approx && /[1-9]/.test(result)) {
            result = `≈ ${result}`;
        }
        return result;
    };

    return {
        localeFormat,
        format,
    };
}

export function usePrimaryCurrencyFormat(options: Options = {}) {
    return useCurrencyFormat(usePrimaryCurrency(), options);
}

type Parts = {
    symbol: string | undefined;
    amount: string;
    code: string;
};
type FormatOptions = {
    approx?: boolean;
    parts?: Array<keyof Parts>;
};
type Options = Omit<Intl.NumberFormatOptions, 'currency' | 'currencyDisplay' | 'style'> & FormatOptions;
